import './underwriting.submission.detail.coverage.scss';

import { Box, IconButton, Typography } from '@mui/material';
import {
  BoxLayout,
  ConvergeAccordion,
  ConvergeColorSlider,
  ConvergeList,
  PaperLayout,
  PrimaryTitle,
  StackLayout,
} from '../../../presentation/components';
import {
  CoverageControlDetails,
  CoverageSubControlDetails,
  EntitiesCoverageControlsData,
  EntitiesCoverageData,
  GetCoveragesQuery,
} from '@converge/graphql';
import {
  SubmissionApplicationQuestionDetailsExt,
  SubmissionQuestionDetails,
} from '../../application';
import { useEffect, useState } from 'react';

import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import { LoadingPageLayout } from '../../../presentation/layouts';
import { UnderwritingSubmissionDetailRiskProfileEvidenceNotesModal } from './underwriting.submission.detail.risk-profile.evidence-notes-modal';

interface IProps {
  data: GetCoveragesQuery;
  submissionUuid: string;
}

interface IEvidenceNotesModalData {
  header: string;
  coverageScore: number | null;
  subHeader: string;
  subControlScore: number | null;
  submissionUuid?: string;
  subControlUuid?: string;
  questions:
    | SubmissionQuestionDetails[]
    | SubmissionApplicationQuestionDetailsExt[];
}

type ExcludableCoverageSubControlDetails = CoverageSubControlDetails & {
  // excluded: boolean;
};

type ExcludableCoverageControlDetails = Omit<
  CoverageControlDetails,
  'subControls'
> & {
  subControls?: ExcludableCoverageSubControlDetails[];
};

interface ExtendedCoveragesData extends Omit<EntitiesCoverageData, 'controls'> {
  expanded: boolean;
  controls: ExcludableCoverageControlDetails[];
}

interface ExtendedControlsData
  extends Omit<EntitiesCoverageControlsData, 'subControls'> {
  expanded: boolean;
  controls?: ExcludableCoverageSubControlDetails[];
}

export const UnderwritingSubmissionDetailCoverage = ({
  data,
  submissionUuid,
}: IProps) => {
  const [expandAll, setExpandAll] = useState<boolean>(false);
  const [coverages, setCoverages] = useState<ExtendedCoveragesData[]>([]);
  const [showEvidenceNotesModal, setShowEvidenceNotesModal] =
    useState<boolean>(false);
  const [evidenceNotesModalData, setEvidenceNotesModalData] =
    useState<IEvidenceNotesModalData>({
      header: '',
      coverageScore: null,
      subHeader: '',
      subControlScore: null,
      submissionUuid: '',
      subControlUuid: '',
      questions: [],
    });

  useEffect(() => {
    if (data && data?.entities.edges?.[0]?.node.coverages) {
      setCoverages(
        data?.entities.edges?.[0]?.node.coverages.edges.map(
          ({ node: coverage }) => ({
            ...coverage,
            controls: coverage.controls.edges.map(({ node: control }) => ({
              ...control,
              subControls: control.subControls.edges.map(
                ({ node: subControl }) => ({
                  ...subControl,
                })
              ),
            })),
            expanded:
              data?.entities.edges?.[0]?.node.coverages.edges.length === 1,
          })
        ) ?? []
      );
    } else {
      setCoverages([]);
    }
  }, [data]);

  const handleExpandCollapseAll = () => {
    setCoverages(
      coverages.map((coverage) => ({
        ...coverage,
        expanded: !expandAll,
      }))
    );
    setExpandAll(!expandAll);
  };

  const handleExpandCollapse = (isExpanded: boolean, id: string) => {
    setCoverages(
      coverages.map((coverage) => {
        if (coverage.uuid === id) {
          return { ...coverage, expanded: isExpanded };
        }
        return coverage;
      })
    );
  };

  const handleShowEvidenceNotes = (
    header: string,
    coverageScore: number,
    subHeader: string,
    subControlScore: number,
    subControlUuid: string,
    submissionUuid: string,
    questions: SubmissionQuestionDetails[]
  ) => {
    setEvidenceNotesModalData({
      header: header,
      coverageScore: coverageScore,
      subHeader: subHeader,
      subControlScore: subControlScore,
      subControlUuid: subControlUuid,
      submissionUuid: submissionUuid,
      questions: questions,
    });
    setShowEvidenceNotesModal(true);
  };

  const handleCloseEvidenceNotesModal = () => {
    setShowEvidenceNotesModal(false);
  };

  return (
    <StackLayout direction="column" flexGrow={1}>
      {coverages.length ? (
        <>
          <StackLayout
            justifyContent="space-between"
            className="expand-button-wrapper"
          >
            <span></span>
            <span onClick={handleExpandCollapseAll}>Expand/Collapse All</span>
          </StackLayout>
          {coverages.map((coverage, index) => (
            <UnderwritingSubmissionDetailRiskProfileCoverage
              coverage={coverage}
              submissionUuid={submissionUuid}
              key={index}
              handleShowEvidenceNotes={handleShowEvidenceNotes}
              handleExpandCollapse={handleExpandCollapse}
            />
          ))}
        </>
      ) : (
        <LoadingPageLayout
          title="Risk profile is processing..."
          titleId="underwriting.riskProfile.noCoverages"
          subtitle="Please wait a moment and reload the page"
          subtitleId="underwriting.riskProfile.noCoverages.subtitle"
        />
      )}
      <UnderwritingSubmissionDetailRiskProfileEvidenceNotesModal
        show={showEvidenceNotesModal}
        onClose={handleCloseEvidenceNotesModal}
        header={evidenceNotesModalData?.header ?? ''}
        coverageScore={evidenceNotesModalData?.coverageScore ?? 0}
        subHeader={evidenceNotesModalData?.subHeader ?? ''}
        subControlScore={evidenceNotesModalData?.subControlScore ?? 0}
        questions={evidenceNotesModalData?.questions ?? []}
        subControlUuid={evidenceNotesModalData.subControlUuid ?? ''}
        submissionUuid={evidenceNotesModalData.submissionUuid ?? ''}
      />
    </StackLayout>
  );
};

const UnderwritingSubmissionDetailRiskProfileCoverage = ({
  coverage,
  handleExpandCollapse,
  submissionUuid,
  handleShowEvidenceNotes,
}: {
  coverage: ExtendedCoveragesData;
  handleExpandCollapse(isExpanded: boolean, id: string): void;
  submissionUuid: string;
  handleShowEvidenceNotes(
    header: string,
    coverageScore: number,
    subHeader: string,
    subControlScore: number,
    subControlUuid: string,
    submissionUuid: string,
    questions: SubmissionQuestionDetails[]
  ): void;
}) => {
  const [controls, setControls] = useState<ExtendedControlsData[]>([]);

  useEffect(() => {
    if (coverage.controls) {
      setControls(
        coverage.controls.map((control) => ({
          ...control,
          expanded: false,
        })) ?? []
      );
    } else {
      setControls([]);
    }
  }, [coverage.controls]);

  const handleExpandCollapseControl = (isExpanded: boolean, id: string) => {
    setControls(
      controls.map((control) => {
        if (control.uuid === id) {
          return { ...control, expanded: isExpanded };
        }
        return control;
      })
    );
  };

  return (
    <ConvergeAccordion
      id={coverage.uuid as string}
      onChange={handleExpandCollapse}
      expanded={coverage.expanded}
      header={
        <StackLayout
          justifyContent="space-between"
          className="coverage-wrapper"
        >
          <PrimaryTitle variant="h4" color="black">
            {coverage.name}
          </PrimaryTitle>
          <ConvergeColorSlider
            min={coverage.minScore || 0}
            max={coverage.maxScore || 100}
            value={coverage.score || 0}
          />
        </StackLayout>
      }
    >
      {controls?.map((control, index) => {
        const opacity = control.excluded ? 0.5 : 1;
        return (
          <Box style={{ marginBottom: '20px' }}>
            <ConvergeAccordion
              id={control.uuid as string}
              onChange={handleExpandCollapseControl}
              expanded={control.expanded}
              header={
                <StackLayout
                  justifyContent="space-between"
                  sx={{ width: '100%', opacity }}
                >
                  <h3 style={{ margin: 10 }}>
                    {control.name} ({control.referenceId}) :
                  </h3>
                  {control.excluded ? (
                    <Typography marginY={1}>N/A</Typography>
                  ) : (
                    <ConvergeColorSlider
                      min={control.minScore || 0}
                      max={control.maxScore || 20}
                      value={control.score || 0}
                      height={12}
                      opacity={0.7}
                    />
                  )}
                </StackLayout>
              }
            >
              <UnderwritingSubmissionDetailRiskProfileCoverageControl
                coverage={coverage}
                control={control}
                submissionUuid={submissionUuid}
                handleShowEvidenceNotes={handleShowEvidenceNotes}
                key={index}
              />
            </ConvergeAccordion>
          </Box>
        );
      })}
    </ConvergeAccordion>
  );
};

const UnderwritingSubmissionDetailRiskProfileCoverageControl = ({
  control,
  coverage,
  submissionUuid,
  handleShowEvidenceNotes,
}: {
  control: ExcludableCoverageControlDetails;
  coverage: ExtendedCoveragesData;
  submissionUuid: string;
  handleShowEvidenceNotes(
    header: string,
    coverageScore: number,
    subHeader: string,
    subControlScore: number,
    subControlUuid: string,
    submissionUuid: string,
    questions: SubmissionQuestionDetails[]
  ): void;
}) => {
  const opacity = control.excluded ? 0.5 : 1;
  return (
    <>
      <BoxLayout sx={{ mx: 3, mt: 2 }} key={control.uuid}>
        {/* <StackLayout
          justifyContent="space-between"
          sx={{ width: '100%', opacity }}
        >
          <h3 style={{ margin: 0 }}>
            {control.name} ({control.referenceId}) :
          </h3>
          {control.excluded ? (
            <Typography marginY={1}>N/A</Typography>
          ) : (
            <ConvergeColorSlider
              min={control.minScore || 0}
              max={control.maxScore || 20}
              value={control.score || 0}
              height={12}
              opacity={0.7}
            />
          )}
        </StackLayout> */}
        <p style={{ opacity }}>{control.description}</p>
        <PaperLayout elevation={2} sx={{ mt: 5, mb: 5 }}>
          <ConvergeList
            items={control.subControls ?? []}
            renderItem={(subControl) => (
              <UnderwritingSubmissionDetailRiskProfileCoverageSubControl
                subControl={subControl}
                submissionUuid={submissionUuid}
                control={control}
                coverage={coverage}
                handleShowEvidenceNotes={handleShowEvidenceNotes}
              />
            )}
          />
        </PaperLayout>
      </BoxLayout>
    </>
  );
};

const UnderwritingSubmissionDetailRiskProfileCoverageSubControl = ({
  coverage,
  control,
  subControl,
  submissionUuid,
  handleShowEvidenceNotes,
}: {
  control: ExcludableCoverageControlDetails;
  subControl: ExcludableCoverageSubControlDetails;
  coverage: ExtendedCoveragesData;
  submissionUuid: string;
  handleShowEvidenceNotes(
    header: string,
    coverageScore: number,
    subHeader: string,
    subControlScore: number,
    subControlUuid: string,
    submissionUuid: string,
    questions: SubmissionQuestionDetails[]
  ): void;
}) => {
  const questions = subControl.questions.edges.map((edge) => edge.node);
  return (
    <StackLayout
      justifyContent="space-between"
      sx={{ width: '100%', opacity: subControl.excluded ? 0.5 : 1 }}
    >
      <div className="sub-control-section">
        <span>
          {subControl.name} ({subControl.referenceId})
        </span>
      </div>
      <StackLayout flexDirection={'row'}>
        {subControl.excluded ? (
          <Typography marginY={1}>N/A</Typography>
        ) : (
          <>
            <IconButton
              onClick={() => {
                handleShowEvidenceNotes(
                  coverage.name ?? '',
                  coverage.score ?? 0,
                  `${control.name} (${control.referenceId}) > ${subControl.name} (${subControl.referenceId})`,
                  subControl.score ?? 0,
                  subControl.uuid ?? '',
                  submissionUuid ?? '',
                  questions
                );
              }}
            >
              <DescriptionOutlined className="evidence-notes-icon" />
            </IconButton>
            <ConvergeColorSlider
              min={subControl.minScore || 0}
              max={
                subControl.maxScore && subControl.maxScore > 0
                  ? subControl.maxScore
                  : 20
              }
              value={subControl.score || 0}
              height={12}
              opacity={0.7}
              gradient="blue"
            />
          </>
        )}
      </StackLayout>
    </StackLayout>
  );
};
